/**
 * SCM — BOM document classifier.
 *
 * Detects bill-of-materials schedules vs vendor quote PDFs. High-precision
 * over high-recall — false positives would run the wrong extractor.
 */
export interface BomDocumentClassification {
  isBomDocument: boolean;
  confidence: number;
  signals: {
    bomHeading: boolean;
    lineItemTable: boolean;
    qtyColumn: boolean;
    equipmentSchedule: boolean;
    quoteLike: boolean;
  };
}

const BOM_HEADING =
  /\b(?:bill\s+of\s+materials?|bom\b|equipment\s+schedule|material\s+take[-\s]?off|procurement\s+schedule|major\s+equipment\s+list)\b/i;

const LINE_ITEM_TABLE =
  /\b(?:line\s*#|item\s*#|qty\.?|quantity|description|unit\s+price|extended\s+price|uom)\b/i;

const QTY_COLUMN = /\b(?:\d+\s+(?:ea|each|lot|mw|mwh|kva|ton|m3|m²|ft)\b)/i;

const EQUIPMENT_SCHEDULE =
  /\b(?:transformer|switchgear|smr|module|bess|megapack|cooling\s+tower|generator|inverter|rack|ups)\b/i;

const QUOTE_LIKE =
  /\b(?:unit\s+price|validity|payment\s+terms|incoterm|quote\s+for\s+rfq|response\s+to\s+rfq)\b/i;

export const BOM_AUTO_EXTRACT_CONFIDENCE_THRESHOLD = 0.45;

export function classifyBomDocument(
  text: string,
  opts?: { fileName?: string },
): BomDocumentClassification {
  const body = text.slice(0, 120_000);
  const fileName = opts?.fileName ?? '';

  const bomHeading = BOM_HEADING.test(body) || /\bbom\b/i.test(fileName);
  const lineItemTable = LINE_ITEM_TABLE.test(body);
  const qtyColumn = QTY_COLUMN.test(body);
  const equipmentSchedule = EQUIPMENT_SCHEDULE.test(body);
  const quoteLike = QUOTE_LIKE.test(body);

  let score = 0;
  if (bomHeading) score += 0.35;
  if (lineItemTable) score += 0.25;
  if (qtyColumn) score += 0.2;
  if (equipmentSchedule) score += 0.15;
  if (quoteLike) score -= 0.35;

  const structuralHits = [bomHeading, lineItemTable, qtyColumn, equipmentSchedule].filter(Boolean).length;
  const isBomDocument =
    score >= BOM_AUTO_EXTRACT_CONFIDENCE_THRESHOLD &&
    structuralHits >= 2 &&
    !quoteLike;

  return {
    isBomDocument,
    confidence: Math.max(0, Math.min(1, score)),
    signals: { bomHeading, lineItemTable, qtyColumn, equipmentSchedule, quoteLike },
  };
}

export function shouldExtractBomFromDocument(classification: BomDocumentClassification): boolean {
  return classification.isBomDocument && classification.confidence >= BOM_AUTO_EXTRACT_CONFIDENCE_THRESHOLD;
}
